import React from 'react';
import {
  Tag, Award, Star, Trophy, Shield, Crown, Heart, Flag, Zap, Circle,
  Square, Diamond, Hexagon, Users, User, Megaphone, Store, Calendar,
  Package, Layers, Building2, Mic, Briefcase, Gift, Gem
} from 'lucide-react';
import { Category } from './types';

interface CategoryBadgeProps {
  category: Category;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

// Icon mapping matching the backend
const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
  // Event-specific icons
  'users': Users,
  'user': User,
  'mic': Mic,
  'megaphone': Megaphone,
  'store': Store,
  'building2': Building2,
  'briefcase': Briefcase,
  'calendar': Calendar,
  'package': Package,
  'gift': Gift,
  
  // Status/tier icons
  'gem': Gem,
  'trophy': Trophy,
  'award': Award,
  'star': Star,
  'crown': Crown,
  'shield': Shield,
  
  // Generic icons
  'tag': Tag,
  'flag': Flag,
  'heart': Heart,
  'zap': Zap,
  'layers': Layers,
  'circle': Circle,
  'square': Square,
  'diamond': Diamond,
  'hexagon': Hexagon,
};

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const CategoryBadge: React.FC<CategoryBadgeProps> = ({ 
  category, 
  size = 'md',
  showIcon = true 
}) => {
  const { color, textColor, badgeStyle, icon } = category.displayProperties || {};
  
  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };
  
  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 16
  };
  
  let style: React.CSSProperties = {};
  let className = `inline-flex items-center gap-1.5 ${sizeClasses[size]} rounded-full transition-colors`;
  
  if (color) {
    if (badgeStyle === 'solid') {
      style = {
        backgroundColor: color,
        color: textColor || '#ffffff',
      };
    } else if (badgeStyle === 'outline') {
      style = {
        backgroundColor: 'transparent',
        color: color,
        border: `1px solid ${color}`,
      };
    } else { // soft style (default)
      style = {
        backgroundColor: hexToRgba(color, 0.1),
        color: color,
      };
    }
  } else {
    // Default style if no custom colors
    className += ' bg-gray-100 text-gray-600';
  }
  
  // Get the icon component
  const IconComponent = icon && showIcon ? ICON_MAP[icon] || Tag : null;
  
  return (
    <span className={className} style={style}>
      {IconComponent && (
        <IconComponent size={iconSizes[size]} className="flex-shrink-0" />
      )}
      <span>{category.name}</span>
    </span>
  );
};